import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

const DEFAULT_SEVERITY = 'info';
const SEVERITY_VARIANTS = {
  success: 'success',
  error: 'error',
  info: 'info',
  warn: 'warning',
  warning: 'warning',
};

const getToastMessage = (message) => {
  if (typeof message === 'string') return message;
  return message?.message || '';
};

/**
 * Exibe notificacoes padronizadas para o usuario via toast.
 *
 * Regra de negocio:
 * - Toda notificacao importante deve passar por este hook
 * - Severidade publica usa success, info, warn ou error
 *
 * @returns {object} Helpers para exibir e fechar toasts
 */
export const useToast = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showToast = useCallback((message, options = {}) => {
    const { severity = DEFAULT_SEVERITY, variant, ...snackbarOptions } = options;
    const toastMessage = getToastMessage(message);

    if (!toastMessage) return null;

    return enqueueSnackbar(toastMessage, {
      ...snackbarOptions,
      variant: variant || SEVERITY_VARIANTS[severity] || SEVERITY_VARIANTS[DEFAULT_SEVERITY],
    });
  }, [enqueueSnackbar]);

  const success = useCallback((message, options = {}) => showToast(message, {
    ...options,
    severity: 'success',
  }), [showToast]);

  const info = useCallback((message, options = {}) => showToast(message, {
    ...options,
    severity: 'info',
  }), [showToast]);

  const warn = useCallback((message, options = {}) => showToast(message, {
    ...options,
    severity: 'warn',
  }), [showToast]);

  const error = useCallback((message, options = {}) => showToast(message, {
    ...options,
    severity: 'error',
  }), [showToast]);

  return {
    showToast,
    toast: showToast,
    success,
    info,
    warn,
    warning: warn,
    error,
    closeToast: closeSnackbar,
  };
};
